import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'

const swiper = {
    calculateSlidesPerView(swiperElement) {
        const windowWidth = window.innerWidth;
        const sliderWidth = swiperElement.offsetWidth;

        // Force 1 slide on smaller than MD screens
        if (windowWidth < 768) {
            return 1.15;
        }

        // Calculate ratio - bigger screen relative to slider = show less slides
        const ratio = windowWidth / sliderWidth;

        // Base slides per view - smaller when screen is bigger relative to slider
        const baseSlidesPerView = Math.max(1.1, Math.min(2.5 / ratio, 3));

        return baseSlidesPerView;
    },
    config: {
        default: {
            modules: [Navigation, Pagination, Autoplay],
            navigation: {
                nextEl: '.swiper-next',
                prevEl: '.swiper-prev',
            },
        },
        imageCarouselAlt: {
            modules: [Navigation, Pagination],
            loop: true,
            centeredSlides: true,
            spaceBetween: 16,
            slidesPerView: 'auto',
            navigation: {
                nextEl: '.swiper-next',
                prevEl: '.swiper-prev',
            },
        },
    },
    activeSwipers: [],
    calculateCarouselHeight(swiperElement) {
        const slides = swiperElement.querySelectorAll('.swiper-slide[data-aspect-ratio]');
        if (slides.length === 0) return 50; // Default to 50vh

        let maxAspectRatio = 0;
        slides.forEach(slide => {
            const aspectRatio = parseFloat(slide.dataset.aspectRatio);
            if (aspectRatio > maxAspectRatio) {
                maxAspectRatio = aspectRatio;
            }
        });

        if (maxAspectRatio === 0) return 50;

        const viewportWidth = window.innerWidth;
        let horizontalMargin = 0;

        if (viewportWidth < 1090) {
            horizontalMargin = 30 * 2; // 1.875rem * 2
        } else if (viewportWidth < 1260) {
            horizontalMargin = 60 * 2; // 3.75rem * 2
        } else if (viewportWidth < 1620) {
            horizontalMargin = 80 * 2; // 5rem * 2
        } else {
            horizontalMargin = (viewportWidth * 0.1) * 2; // 10vw * 2
        }

        const availableWidth = viewportWidth - horizontalMargin;
        const viewportHeight = window.innerHeight;
        const maxHeight = (availableWidth / maxAspectRatio) * 100 / viewportHeight;
        const maxHeightVh = Math.min(maxHeight, 50); // Cap at 50vh

        return maxHeightVh;
    },
    updateCarouselHeight(swiperElement) {
        const heightVh = this.calculateCarouselHeight(swiperElement);
        swiperElement.style.setProperty('--carousel-height', `${heightVh}vh`);
    },
    initImageSliderControls(swiperElement, swiperInstance) {
        // Anchor the controls to the first image so caption height cannot move them between slides.
        const media = swiperElement.querySelector('.swiper-slide figure');

        if (!media) return;

        const updateControls = () => {
            const sliderRect = swiperElement.getBoundingClientRect();
            const mediaRect = media.getBoundingClientRect();
            const mediaCenter = mediaRect.top - sliderRect.top + (mediaRect.height / 2);
            const mediaBottom = mediaRect.bottom - sliderRect.top;

            // Use image boundaries rather than the full slider height, which also includes the caption.
            swiperElement.style.setProperty('--image-slider-nav-center', `${mediaCenter}px`);
            swiperElement.style.setProperty('--image-slider-media-bottom', `${mediaBottom}px`);
        };

        updateControls();

        // Observe only the image area so responsive resizing is handled without reacting to caption reflow.
        const resizeObserver = new ResizeObserver(updateControls);
        resizeObserver.observe(media);

        swiperInstance.on('destroy', () => resizeObserver.disconnect());
    },
    initImageSliderAutoplay(swiperElement, swiperInstance) {
        const toggle = swiperElement.querySelector('[data-swiper-autoplay-toggle]');

        if (!toggle || !swiperInstance.autoplay) return;

        const playIcon = toggle.querySelector('[data-swiper-autoplay-play]');
        const pauseIcon = toggle.querySelector('[data-swiper-autoplay-pause]');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        const updateToggle = () => {
            const isPlaying = swiperInstance.autoplay.running;

            toggle.setAttribute('aria-label', isPlaying ? 'Pause carousel' : 'Play carousel');
            playIcon.hidden = isPlaying;
            pauseIcon.hidden = !isPlaying;
            toggle.hidden = false;
        };

        const toggleAutoplay = () => {
            if (swiperInstance.autoplay.running) {
                swiperInstance.autoplay.stop();
            } else {
                swiperInstance.autoplay.start();
            }

            updateToggle();
        };

        const handleReducedMotion = event => {
            if (!event.matches) return;

            swiperInstance.autoplay.stop();
            updateToggle();
        };

        toggle.addEventListener('click', toggleAutoplay);
        reducedMotion.addEventListener('change', handleReducedMotion);

        // Start paused for reduced motion, while allowing the visitor to play it explicitly.
        if (reducedMotion.matches) {
            swiperInstance.autoplay.stop();
        }

        updateToggle();

        swiperInstance.on('destroy', () => {
            toggle.removeEventListener('click', toggleAutoplay);
            reducedMotion.removeEventListener('change', handleReducedMotion);
        });
    },
    create(swiperElement) {
        const handle = swiperElement.dataset.swiperHandle ?? 'default';
        const config = { ...this.config[handle] };
        const autoplayEnabled = swiperElement.dataset.autoplay === 'true';

        if (autoplayEnabled) {
            config.autoplay = {
                delay: 6000,
                // Arrow navigation should not override the visitor's current play/pause choice.
                disableOnInteraction: false,
            };
        }

        const instance = new Swiper(swiperElement, config);

        if (handle === 'imageCarouselAlt') {
            const updateHeight = () => {
                this.updateCarouselHeight(swiperElement);
                instance.update();
            };

            updateHeight();
            window.addEventListener('resize', updateHeight);
            instance.on('destroy', () => window.removeEventListener('resize', updateHeight));
        }

        if (swiperElement.classList.contains('swiper--image-carousel')) {
            this.initImageSliderControls(swiperElement, instance);

            if (autoplayEnabled) {
                this.initImageSliderAutoplay(swiperElement, instance);
            }
        }

        this.activeSwipers.push(instance);

        return instance;
    },
    init() {
        const swiperElements = document.querySelectorAll('.swiper')
        if (swiperElements.length === 0) return;

        swiperElements.forEach(swiperElement => {
            this.create(swiperElement);
        });
    },
};

export default swiper;
